import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AcademyMetrics {
  totalTrials: number;
  activeTrials: number;
  expiredTrials: number;
  totalPremium: number;
  monthlyPremium: number;
  yearlyPremium: number;
  conversionRate: number;
  newTrialsLast7d: number;
  newTrialsLast30d: number;
  newPremiumLast30d: number;
  estimatedMRR: number;
}

export interface AcademySubscriber {
  user_id: string;
  full_name: string | null;
  email: string | null;
  plan_type: string;
  status: string;
  billing_cycle: string | null;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  premium_started_at: string | null;
  premium_ends_at: string | null;
  created_at: string;
}

export function useAcademyMetrics() {
  return useQuery({
    queryKey: ["academy-metrics"],
    queryFn: async (): Promise<{ metrics: AcademyMetrics; recent: AcademySubscriber[] }> => {
      const now = new Date();
      const iso7 = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const iso30 = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const nowIso = now.toISOString();

      const { data: subs, error } = await supabase
        .from("user_subscriptions")
        .select(
          "user_id, plan_type, status, billing_cycle, trial_started_at, trial_ends_at, premium_started_at, premium_ends_at, created_at"
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      const rows = subs || [];

      const totalTrials = rows.filter((r) => r.trial_started_at).length;
      const activeTrials = rows.filter(
        (r) =>
          r.plan_type === "trial" &&
          r.trial_ends_at &&
          new Date(r.trial_ends_at) > now
      ).length;
      const expiredTrials = rows.filter(
        (r) =>
          r.trial_started_at &&
          r.trial_ends_at &&
          new Date(r.trial_ends_at) <= now &&
          r.plan_type !== "premium"
      ).length;

      const premiumRows = rows.filter(
        (r) =>
          r.plan_type === "premium" &&
          r.premium_ends_at &&
          new Date(r.premium_ends_at) > now
      );
      const totalPremium = premiumRows.length;
      const monthlyPremium = premiumRows.filter((r) => r.billing_cycle === "monthly").length;
      const yearlyPremium = premiumRows.filter((r) => r.billing_cycle === "yearly").length;

      const converted = rows.filter(
        (r) => r.trial_started_at && r.plan_type === "premium"
      ).length;
      const conversionRate = totalTrials > 0 ? (converted / totalTrials) * 100 : 0;

      const newTrialsLast7d = rows.filter(
        (r) => r.trial_started_at && r.trial_started_at >= iso7
      ).length;
      const newTrialsLast30d = rows.filter(
        (r) => r.trial_started_at && r.trial_started_at >= iso30
      ).length;
      const newPremiumLast30d = rows.filter(
        (r) => r.premium_started_at && r.premium_started_at >= iso30
      ).length;

      const estimatedMRR = monthlyPremium * 49 + yearlyPremium * 32.5;

      // Recentes com email
      const userIds = rows.slice(0, 20).map((r) => r.user_id);
      let emailsMap: Record<string, { full_name: string | null; email: string | null }> = {};
      if (userIds.length > 0) {
        const { data: emails } = await supabase.rpc("admin_get_user_emails", {
          p_user_ids: userIds,
        });
        (emails || []).forEach((e: any) => {
          emailsMap[e.id] = { full_name: e.full_name, email: e.email };
        });
      }

      const recent: AcademySubscriber[] = rows.slice(0, 20).map((r) => ({
        ...r,
        full_name: emailsMap[r.user_id]?.full_name || null,
        email: emailsMap[r.user_id]?.email || null,
      }));

      return {
        metrics: {
          totalTrials,
          activeTrials,
          expiredTrials,
          totalPremium,
          monthlyPremium,
          yearlyPremium,
          conversionRate,
          newTrialsLast7d,
          newTrialsLast30d,
          newPremiumLast30d,
          estimatedMRR,
        },
        recent,
      };
    },
    staleTime: 60 * 1000,
  });
}
